import React from 'react';
import { Body5, Body7, Checkbox, ConfirmModal, Container, TextField } from '@ui';
import { FlatButton } from '@ui/buttons';
import { DragDrop } from '@ui/DragAndDropUploader';
import ErrorText from '@ui/ErrorText';
import {
  fileExtensionOf,
  setupTenant,
  TEMPLATE_MAX_BYTES,
  TEMPLATE_VERSION_PATTERN,
  TENANT_NAME_MAX,
} from '@services/fmdc';
import { formatBytes } from './format';
import { NoticeText, NOTICE_TONES } from './NoticeText';
import { Card, FileChip, FileChipRemoveButton, SectionHead } from './styles';

// The client setup form: the extraction template, its version, a name, and whether to embed the
// catalog. POST /tenant/setup answers 202; the tenant context then polls GET /tenant until
// `setup.status` settles, so this form's job ends at "queued".
//
// Three states the form can be in beyond "ready":
// - blocked (a setup is already queued or running): the button is inert with a hint, the
//   ScoreBackfillForm not-ready treatment, because the server answers 409 anyway;
// - stale (no progress for 30 minutes): the same form, but it says so and offers "Request again";
// - upgrade (the client already has a template): submit goes behind a confirm that says what an
//   upgrade keeps and what a failed one does.

export const templateFileProblem = (file) => {
  if (!file) return null;
  if (fileExtensionOf(file) !== 'xlsx') return 'The template must be an .xlsx file.';
  if (file.size > TEMPLATE_MAX_BYTES) return `The template must be ${formatBytes(TEMPLATE_MAX_BYTES)} or smaller.`;
  return null;
};

// The first thing stopping a submit, or null. One function so the hint under the button and the
// handler's guard cannot disagree.
export const setupValidationMessage = ({ file, templateVersion, name }) => {
  if (!file) return 'Drop the client’s extraction template to continue.';
  const fileProblem = templateFileProblem(file);
  if (fileProblem) return fileProblem;
  const version = (templateVersion || '').trim();
  if (!version) return 'Enter the template version, e.g. KS-V1.';
  if (!TEMPLATE_VERSION_PATTERN.test(version)) {
    return 'Template version: letters, digits, . _ or -, up to 32 characters.';
  }
  if ((name || '').trim().length > TENANT_NAME_MAX) return `Name must be ${TENANT_NAME_MAX} characters or fewer.`;
  return null;
};

export const SetupForm = ({ hasTemplate = false, blockedReason = null, isStale = false, onQueued }) => {
  const [file, setFile] = React.useState(null);
  const [templateVersion, setTemplateVersion] = React.useState('');
  const [name, setName] = React.useState('');
  const [embedCatalog, setEmbedCatalog] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [queued, setQueued] = React.useState(false);
  const [confirmingUpgrade, setConfirmingUpgrade] = React.useState(false);

  const fileProblem = templateFileProblem(file);
  const versionTouched = templateVersion !== '';
  const versionInvalid = versionTouched && !TEMPLATE_VERSION_PATTERN.test(templateVersion.trim());
  const nameInvalid = name.trim().length > TENANT_NAME_MAX;
  const validation = setupValidationMessage({ file, templateVersion, name });
  const canSubmit = !busy && !blockedReason && !validation;

  const send = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setQueued(false);
    const result = await setupTenant({
      template: file,
      templateVersion: templateVersion.trim(),
      name: name.trim() || undefined,
      embedCatalog,
    });
    setBusy(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setQueued(true);
    if (onQueued) onQueued(result.setup);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (hasTemplate) {
      setConfirmingUpgrade(true);
      return;
    }
    send();
  };

  const handleAddFiles = (files) => {
    // The dropzone is always multiple; the template is one file, so the last one dropped wins.
    const last = files && files.length ? files[files.length - 1] : null;
    setFile(last ? last.file : null);
    setError(null);
    setQueued(false);
  };

  const heading = hasTemplate ? 'Upgrade the template' : 'Set up data capture';
  const subtext = hasTemplate
    ? 'Running setup again with a new template version moves the client to it. Items added or edited in the catalog are kept.'
    : 'Upload the client’s extraction template. Setup builds the catalog from it and takes about ten seconds once picked up.';
  const buttonLabel = busy
    ? 'Uploading…'
    : isStale
    ? 'Request again'
    : hasTemplate
    ? 'Upgrade to this template'
    : 'Set up';
  const hint = blockedReason || validation;

  return (
    <Card>
      <SectionHead heading={heading} subtext={subtext} />

      {isStale && (
        <Container top={12}>
          <NoticeText tone={NOTICE_TONES.WARNING}>
            Setup appears stuck: no progress for 30 minutes. Requesting it again starts a fresh one.
          </NoticeText>
        </Container>
      )}

      <Container top={16}>
        <Body7>Extraction template (.xlsx, up to {formatBytes(TEMPLATE_MAX_BYTES)})</Body7>
        <Container top={6}>
          <DragDrop onAddFiles={handleAddFiles} xlsx includePdf={false} />
        </Container>
        {file && (
          <Container top={8}>
            <FileChip>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
              <Body5 truncate title={file.name}>
                {file.name}
              </Body5>
              <Body7 isLowContrast>{formatBytes(file.size)}</Body7>
              <FileChipRemoveButton aria-label="Remove template" onClick={() => setFile(null)}>
                <span aria-hidden="true">×</span>
              </FileChipRemoveButton>
            </FileChip>
          </Container>
        )}
        {fileProblem && (
          <Container top={8}>
            <ErrorText>{fileProblem}</ErrorText>
          </Container>
        )}
      </Container>

      <Container top={16}>
        <TextField
          label="Template version"
          value={templateVersion}
          onChange={setTemplateVersion}
          mono
          invalid={versionInvalid}
          hint={
            versionInvalid
              ? 'Letters, digits, . _ or -, up to 32 characters.'
              : 'Names this template in every run and download, e.g. KS-V1.'
          }
        />
      </Container>

      <Container top={16}>
        <TextField
          label="Client name"
          value={name}
          onChange={setName}
          invalid={nameInvalid}
          hint={nameInvalid ? `Up to ${TENANT_NAME_MAX} characters.` : 'Optional.'}
        />
      </Container>

      <Container top={16}>
        <Checkbox
          name="embed-catalog"
          label="Embed the catalog after setup"
          value={embedCatalog}
          onChange={() => (next) => setEmbedCatalog(next)}
        />
        <Container top={4}>
          <Body7>Embeddings are what kNN matching reads. Leave this on unless you are testing.</Body7>
        </Container>
      </Container>

      {error && (
        <Container top={16}>
          <ErrorText>{error}</ErrorText>
        </Container>
      )}

      {queued && !error && (
        <Container top={16}>
          <NoticeText tone={NOTICE_TONES.POSITIVE}>Setup queued. This page updates when it finishes.</NoticeText>
        </Container>
      )}

      <Container top={24} row verticallyCenter flexWrap gap={12}>
        <FlatButton onClick={handleSubmit} disabled={!canSubmit} buttonProps={{ 'aria-disabled': !canSubmit }}>
          {buttonLabel}
        </FlatButton>
        {hint && !busy && <Body7 isLowContrast>{hint}</Body7>}
      </Container>

      {confirmingUpgrade && (
        <ConfirmModal
          title="Upgrade to this template?"
          body={`The client moves to template ${templateVersion.trim()}. Catalog items added or edited here are kept as they are. If the setup fails, the client stays on its current template.`}
          confirmLabel="Upgrade"
          busy={busy}
          onConfirm={async () => {
            await send();
            setConfirmingUpgrade(false);
          }}
          onCancel={() => setConfirmingUpgrade(false)}
        />
      )}
    </Card>
  );
};
